import type { FC } from 'react'

import { useId, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

export type AccordionItem = {
    question: string
    answer: string
}

interface AccordionProps {
    items: Array<AccordionItem>
    defaultOpen?: number | null
    className?: string
}

export const Accordion: FC<AccordionProps> = ({ items, defaultOpen = 0, className }) => {
    const [open, setOpen] = useState<number | null>(defaultOpen)
    const baseId = useId()

    return (
        <div className={cn('border-t border-ink/10', className)}>
            {items.map((item, index) => {
                const isOpen = open === index
                const buttonId = `${baseId}-q-${index}`
                const panelId = `${baseId}-a-${index}`

                return (
                    <div
                        key={item.question}
                        className='border-b border-ink/10'
                    >
                        <h3 className='font-sans tracking-normal'>
                            <button
                                id={buttonId}
                                type='button'
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => setOpen(isOpen ? null : index)}
                                className='group flex w-full items-center justify-between gap-6 py-6 text-left md:py-7'
                            >
                                <span className='text-lg font-semibold tracking-tight text-ink md:text-xl'>
                                    {item.question}
                                </span>

                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'flex size-10 shrink-0 items-center justify-center rounded-full border transition-[transform,background-color,border-color] duration-500 ease-expo',
                                        isOpen ? 'rotate-[135deg] border-ember bg-ember text-cream' : 'border-ink/15 text-ink group-hover:border-ink/50'
                                    )}
                                >
                                    <Plus className='size-4' />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={panelId}
                            role='region'
                            aria-labelledby={buttonId}
                            inert={!isOpen}
                            className={cn('grid transition-[grid-template-rows,opacity] duration-500 ease-expo', isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}
                        >
                            <div className='overflow-hidden'>
                                <p className='max-w-2xl pb-7 pr-12 text-base leading-relaxed text-stone md:text-lg'>
                                    {item.answer}
                                </p>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
